/**
 * `pan review restart <id> [--model <model>]`
 *
 * Kills all running reviewer + coordinator sessions for an issue, then
 * dispatches a fresh review pipeline. When `--model` is provided, every
 * reviewer spawns with that model instead of the configured per-role models.
 *
 * This is the CLI equivalent of the dashboard's right-click → "Restart all
 * reviewers (with model)" context menu.
 */

import chalk from 'chalk';
import { getDashboardApiUrlSync } from '../../lib/config.js';
import { resolveProjectFromIssueSync } from '../../lib/projects.js';
import { resolveBareNumericIdSync } from '../../lib/issue-id.js';

const DASHBOARD_URL = getDashboardApiUrlSync();

export interface ReviewRestartOptions {
  model?: string;
  role?: string;
}

type ReviewRestartResult = {
  success: boolean;
  message?: string;
  error?: string;
  killed?: string[] | number;
  model?: string;
};

function previewResponseBody(body: string): string {
  const trimmed = body.trim();
  if (!trimmed) return '';
  return trimmed.length > 500 ? `${trimmed.slice(0, 500)}...` : trimmed;
}

function parseReviewRestartResponse(body: string, ok: boolean): ReviewRestartResult {
  const trimmed = body.trim();
  if (!trimmed) {
    return ok
      ? { success: true }
      : { success: false, error: 'Failed to restart review' };
  }

  try {
    return JSON.parse(trimmed) as ReviewRestartResult;
  } catch (error) {
    const preview = previewResponseBody(trimmed);
    if (ok) {
      return {
        success: true,
        message: preview ? `Dashboard accepted the restart but returned a non-JSON response: ${preview}` : undefined,
      };
    }
    return {
      success: false,
      error: preview || (error instanceof Error ? error.message : 'Failed to restart review'),
    };
  }
}

export async function reviewRestartCommand(
  id: string,
  opts: ReviewRestartOptions = {},
): Promise<void> {
  const issueId = resolveBareNumericIdSync(id);
  if (!issueId) {
    console.error(chalk.red(`Could not resolve issue ID "${id}"`));
    console.error(chalk.dim(
      'Pass a fully-qualified ID like "PAN-1148", or ensure the agent state dir exists at ~/.overdeck/agents/agent-<prefix>-<num>/',
    ));
    process.exit(1);
  }

  const resolved = resolveProjectFromIssueSync(issueId);
  if (!resolved) {
    console.error(chalk.red(`\nError: cannot resolve project for ${issueId}`));
    process.exit(1);
  }

  const modelLabel = opts.model ? ` with model ${chalk.cyan(opts.model)}` : '';
  const roleLabel = opts.role ? ` (role: ${opts.role})` : '';
  console.log(chalk.dim(`Restarting review for ${issueId}${roleLabel}${modelLabel}...`));

  try {
    const endpoint = opts.role
      ? `${DASHBOARD_URL}/api/specialists/${resolved.projectKey}/${issueId}/reviewer/${opts.role}/restart`
      : `${DASHBOARD_URL}/api/specialists/${resolved.projectKey}/${issueId}/review/restart`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: opts.model }),
    });

    const result = parseReviewRestartResponse(await response.text(), response.ok);

    if (!response.ok) {
      console.error(chalk.red(`\nError: ${result.error || 'Failed to restart review'}`));
      process.exit(1);
    }

    console.log(chalk.green(`\n✓ Review restarted for ${issueId}`));
    if (result.message) console.log(chalk.dim(`  ${result.message}`));
    if (result.model) console.log(chalk.dim(`  model: ${result.model}`));
  } catch (error: any) {
    if (error.code === 'ECONNREFUSED') {
      console.error(chalk.red('\nError: Dashboard not running'));
      console.error(chalk.dim('Start the dashboard with: pan up'));
      process.exit(1);
    }
    console.error(chalk.red(`\nError: ${error.message}`));
    process.exit(1);
  }
}
